<template>
  <div :id="`${source}_user_actions`" class="w-full">
    <div
      :id="`${source}_user_actions_body`"
      class="flex w-full justify-end px-3"
    >
      <div v-if="isCompleted">
        <CustomDropdown
          v-if="actions?.length > 0"
          gap="4px"
          position="end"
          :relative-to-viewport="true"
        >
          <template #trigger>
            <ThemeImage
              :icon="imageNames.MORE_VERTICAL"
              alt="more_icon"
              class="size-5 cursor-pointer opacity-60"
            />
          </template>

          <template #default>
            <div
              v-for="(action, actionIndex) in actions"
              :key="actionIndex"
              @click="action.handler(row)"
              class="dropdown_item text-sm"
            >
              <ThemeImage
                v-if="action.label === 'Add Action'"
                class="size-4"
                :icon="action.icon"
                alt="action_option"
              />
              <ThemeImage
                v-else
                class="size-3"
                :icon="action.icon"
                alt="action_option"
              />
              {{ action.label }}
            </div>
          </template>
        </CustomDropdown>
      </div>

      <div
        v-else-if="!isExpired"
        class="flex flex-row items-center gap-1 cursor-pointer"
      >
        <span class="text-[13px] revoke_text">Invite Pending</span>
        <CustomDropdown
          v-if="actions?.length > 0"
          gap="4px"
          position="end"
          :relative-to-viewport="true"
        >
          <template #trigger>
            <ThemeImage
              :icon="imageNames.MORE_VERTICAL"
              alt="more_icon"
              class="size-5 cursor-pointer opacity-60"
            />
          </template>

          <template #default>
            <div
              v-for="(action, actionIndex) in invitePendingActions"
              :key="actionIndex"
              @click="action.handler(row)"
              class="dropdown_item text-sm"
            >
              <ThemeImage
                v-if="action.label === 'Add Action'"
                class="size-4"
                :icon="action.icon"
                alt="action_option"
              />
              <ThemeImage
                v-else
                class="size-3"
                :icon="action.icon"
                alt="action_option"
              />
              {{ action.label }}
            </div>
          </template>
        </CustomDropdown>
      </div>

      <div v-else class="flex flex-row items-center gap-1 cursor-pointer">
        <span class="text-[13px] revoke_text">Invite Expired</span>
        <CustomDropdown
          v-if="actions?.length > 0"
          gap="4px"
          position="end"
          :relative-to-viewport="true"
        >
          <template #trigger>
            <ThemeImage
              :icon="imageNames.MORE_VERTICAL"
              alt="more_icon"
              class="size-5 cursor-pointer opacity-60"
            />
          </template>

          <template #default>
            <div
              v-for="(action, actionIndex) in inviteExpiredActions"
              :key="actionIndex"
              @click="action.handler(row)"
              class="dropdown_item text-sm"
            >
              <ThemeImage
                v-if="action.label === 'Add Action'"
                class="size-4"
                :icon="action.icon"
                alt="action_option"
              />
              <ThemeImage
                v-else
                class="size-3"
                :icon="action.icon"
                alt="action_option"
              />
              {{ action.label }}
            </div>
          </template>
        </CustomDropdown>
      </div>
    </div>

    <UserModal
      v-model="editModalVisible"
      :is-editing="true"
      :user-id="row.id"
      :initial-role-id="row.role_id"
      :roles="roles"
    />

    <ManageCreditLimitModal
      v-model="creditLimitModalVisible"
      :row="row"
      @updated="handleBudgetUpdated"
    />

    <CustomModal
      v-model:visible="removeUserModalVisible"
      title="Remove User"
      width="32vw"
    >
      <span class="text_color"
        >Are you sure you want to remove this user? This action cannot be
        undone.</span
      >
      <template #footer>
        <a-button
          class="secondary_medium mr-2"
          @click="() => (removeUserModalVisible = false)"
          >No</a-button
        >
        <a-button class="primary_medium" @click="handleRemoveUser"
          >Yes</a-button
        >
      </template>
    </CustomModal>
  </div>
</template>

<script setup>
import { ref, watch, computed } from "vue";
import { Modal, message } from "ant-design-vue";
import UserModal from "./UserModal.vue";
import ManageCreditLimitModal from "./ManageCreditLimitModal.vue";
import imagePath from "@/src/paths/imagePath";
import eventBus from "@/src/utils/eventBus.js";
import CustomDropdown from "@/src/components/CustomDropdown.vue";
import DashboardService from "@/src/api/DashboardService.js";
import AuthPermissionService from "@/src/api/AuthPermissionService.js";
import store from "@/src/store/index.js";
import { billingStatues } from "@/src/constants/billingConstants.js";
import { isABACEnabled } from "@/src/utils/dynamicFormUtils.js";

import CustomModal from "@/src/components/CustomModal.vue";
import { imageNames } from "@/src/constants/imageConstants";
import BillingService from "@/src/api/BillingService.js";

const props = defineProps({
  source: { type: String, default: "custom_table" },
  row: { type: Object, required: true },
});

const editModalVisible = ref(false);
const creditLimitModalVisible = ref(false);
const roles = ref([]);
const removeUserModalVisible = ref(false);
const selectedUser = ref(null);

const isCompleted = ref(!props.row?.pending_invite);
const isExpired = ref(props.row?.invite_expired);

const humanSeats = computed(() => store.getters["billing/humanSeats"] || 0);
const billingStatus = computed(() => store.getters["billing/status"]);
const userSeatEnabled = computed(
  () => store.getters["billing/userSeatEnabled"]
);

const handleRemoveUser = () => {
  if (isCompleted.value) {
    toRemoveUser(selectedUser.value?.id);
  } else if (isExpired.value) {
    toRemoveInvite(selectedUser.value);
  }
};

const handleRemoveUserClick = (user) => {
  selectedUser.value = user;
  removeUserModalVisible.value = true;
};

const showEditModal = async (user) => {
  try {
    await toFetchRoles();
    editModalVisible.value = true;
  } catch (error) {
    console.error("An error occurred while showing edit modal: ", error);
  }
};

const showManageCreditLimitModal = () => {
  creditLimitModalVisible.value = true;
};

// Relay modal's save event to the parent Users.vue via eventBus so the row updates in place.
const handleBudgetUpdated = (payload) => {
  eventBus.emit("user-budget-updated", payload);
};
const revokeUserInvite = async () => {
  try {
    await toRevokeUserInvite();
  } catch (error) {
    console.error("An error occurred while showing edit modal: ", error);
  }
};

const resendUserInvite = async () => {
  try {
    const payload = {
      email: props.row?.email || "",
      role_id: props.row?.role_id || null,
    };

    const response = await DashboardService.revokeUserInvite(payload);
    if (response?.success) {
      await toResendUserInvite();
    }
  } catch (error) {
    console.error("An error occurred while showing edit modal: ", error);
  }
};

const actions = [
  {
    label: "Switch Role",
    handler: showEditModal,
    icon: imageNames.SWITCH_GREY,
  },
  {
    label: "Manage Credit Limit",
    handler: showManageCreditLimitModal,
    icon: imageNames.EDIT,
  },
  {
    label: "Remove user",
    handler: handleRemoveUserClick,
    icon: imageNames.NOT_ALLOWED_GREY,
  },
];

const invitePendingActions = [
  {
    label: "Resend Invite",
    handler: resendUserInvite,
    icon: imageNames.SEND_GREY,
  },
  {
    label: "Revoke Invite",
    handler: revokeUserInvite,
    icon: imageNames.REVOKE_GREY,
  },
];

const inviteExpiredActions = [
  {
    label: "Resend Invite",
    handler: resendUserInvite,
    icon: imageNames.SEND_GREY,
  },
  {
    label: "Remove user",
    handler: handleRemoveUserClick,
    icon: imageNames.NOT_ALLOWED_GREY,
  },
];

const toFetchRoles = async () => {
  try {
    let response;

    if (isABACEnabled()) {
      response = await AuthPermissionService.fetchRoles();
      if (response.data) {
        roles.value = response.data.map((role) => ({
          id: role.id,
          role_name: role.attributes?.name,
        }));
      }
    } else {
      response = await DashboardService.fetchRoles();
      if (response?.success) {
        roles.value = response.roles;
      }
    }
  } catch (error) {
    message.error("Failed to fetch roles");
  }
};

const toRemoveUser = async (userId) => {
  try {
    const response = await DashboardService.removeUser(userId);
    if (response?.success) {
      message.success("User deleted successfully");
      await Promise.all([
        store.dispatch("users/fetchUsers"),
        store.dispatch("users/fetchUsersWithDeleted"),
      ]);
      eventBus.emit("user-altered");
    } else {
      message.error("Failed to delete user");
    }
  } catch (error) {
    message.error("An error occurred while deleting the user");
  }
};

const toRemoveInvite = async (user) => {
  try {
    const payload = { email: user.email, role_id: user.role_id };
    const response = await DashboardService.removeUserInvite(payload);
    if (response?.success) {
      message.success("User deleted successfully");
      await Promise.all([
        store.dispatch("users/fetchUsers"),
        store.dispatch("users/fetchUsersWithDeleted"),
      ]);
      eventBus.emit("user-altered");
    } else {
      message.error("Failed to delete user");
    }
  } catch (error) {
    message.error("An error occurred while deleting the user");
  }
};

const toRevokeUserInvite = async () => {
  try {
    const payload = {
      email: props.row?.email || "",
      role_id: props.row?.role_id || null,
    };

    const response = await DashboardService.revokeUserInvite(payload);
    if (response?.success) {
      message.success("User invite revoked successfully");
      await Promise.all([
        store.dispatch("users/fetchUsers"),
        store.dispatch("users/fetchUsersWithDeleted"),
      ]);
      eventBus.emit("user-altered");
    }
  } catch (error) {
    console.error("An error occurred while revoking user invite: ", error);
  }
};

const toResendUserInvite = async () => {
  try {
    const payload = {
      emails: props.row?.email ? [props.row.email] : [],
      role_id: props.row?.role_id || null,
    };

    let additionalSeatsNeeded = 0;
    if (billingStatus.value === billingStatues.PAID && userSeatEnabled.value) {
      const response = await DashboardService.fetchUsers();
      if (response?.success) {
        const currentUserCount = response.users?.length || 0;
        const remainingSeats = Math.max(0, humanSeats.value - currentUserCount);
        additionalSeatsNeeded = remainingSeats > 0 ? 0 : 1;
      }
    }

    let isPaymentDue = await checkPaymentDue();
    if (isPaymentDue === true && additionalSeatsNeeded > 0) {
      console.error(
        "Your payment is due. Please complete payment to invite users."
      );
      message.error(
        "Your payment is due. Please complete payment to invite users."
      );
      return;
    }

    const response = await DashboardService.sendTeamInvites(payload);
    if (response?.success) {
      message.success("Invite sent successfully");
      eventBus.emit("user-altered");
    } else if (response?.payment_failed === true) {
      console.error("Payment failed. Unable to invite users");
      message.error("Payment failed. Unable to invite users");
    } else {
      message.error(response?.message);
    }
  } catch (error) {
    console.error("An error occurred while sending invite ");
  }
};

const checkPaymentDue = async () => {
  try {
    const response = await BillingService.checkPaymentDue();
    return response?.success && response?.payment_failed === true;
  } catch (error) {
    console.error("Error fetching payment due status:", error);
  }
};

watch(
  () => removeUserModalVisible.value,
  (newVal) => {
    if (!newVal) {
      selectedUser.value = null;
    }
  },
  { immediate: true }
);
</script>

<style scoped>
.revoke_text {
  color: var(--revoke-color);
}
</style>
